// frontend/src/pages/MyAssets.js
/**
 * Employee Self-Service: My Assets Page
 * 
 * Allows employees to:
 * - View all assets assigned to them
 * - Initiate transfers to other employees
 * - Accept or reject incoming transfers
 * - View pending outgoing transfers
 * 
 * Theme: Navy Blue (Employee)
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useDebounce } from '../hooks/useDebounce';
import { 
  Package, Laptop, Key, Car, FileKey, 
  ArrowLeftRight, CheckCircle, XCircle, Clock, 
  Send, Inbox, AlertCircle, AlertTriangle, User, Calendar,
  ChevronRight, Search, X, CreditCard, Fuel, DoorOpen, Smartphone,
  CornerDownLeft, RotateCcw, ClipboardCheck, ImagePlus, Camera, Upload, FileText,
  Download, Wrench, Check, Siren,
} from 'lucide-react';
import api from '../services/api';
import LetterPreviewModal from '../components/LetterPreviewModal';
import TransferDetailModal from '../components/TransferDetailModal';
import Modal from '../components/UI/Modal';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { CountBadge } from '../components/UI/CountBadge';
import { InfoBadge } from '../components/UI/InfoBadge';
import { deviceLineLabel, isDeviceMobileLine } from '../utils/mobileLineDisplay';
import { useTabPersistence } from '../hooks/usePersistence';
import { useAuth } from '../contexts/AuthContext';
import FormSelect from '../components/UI/FormSelect';
import SearchableCombobox from '../components/UI/SearchableCombobox';
import SearchableMultiCombobox from '../components/UI/SearchableMultiCombobox';
import DebouncedSearchInput from '../components/UI/DebouncedSearchInput';
import {
  useMyAssetsSummary,
  useInitiateTransfer,
  useAcceptTransfer,
  useAcknowledgeReceipt,
  useRejectTransfer,
  useCancelTransfer,
  useAssetDetail,
  useInitiateReturn,
  useCancelReturn,
  useTransactionHistory,
  useWorkshops,
  useServiceParts,
  useSendToWorkshop,
  useReturnFromWorkshop,
  useMyInspectionsDue,
  useSubmitVehicleInspection,
} from '../hooks/useAssetInventoryData';
import SignatureCanvas from '../components/AttendanceEvents/SignatureCanvas';
import { useStaffList } from '../hooks/useStaffData';
import AssetDetailModal from '../components/AssetDetailModal';
import MyTrafficOffences from '../components/asset/MyTrafficOffences';
import MyAccidents from '../components/asset/MyAccidents';
import MyInspectionsDue from '../components/asset/MyInspectionsDue';
import InspectVehicleModal from '../components/asset/InspectVehicleModal';
import ReportAccidentModal from '../components/asset/ReportAccidentModal';
import { getCategoryColorByCode } from '../utils/assetStatusColors';
import {
  canReturnAssetToInventory,
  isSoftwareLicenseAsset,
  requiresPhysicalHandover,
} from '../utils/assetAccess';
import toast from '../utils/toast';
import ConfirmModal from '../components/UI/ConfirmModal';
import { StatusBadge } from '../components/UI/StatusBadge';
import { formatDate } from '../utils/dateUtils';
import { resolveTransferTypeLabel } from '../utils/transferTypeMeta';

const ACK_MAX_PHOTOS = 5;
const ACK_MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const ACK_MAX_PHOTOS_TOTAL_BYTES = 25 * 1024 * 1024;
const assetDisplayName = (asset) => asset?.display_name || asset?.asset_tag || 'Asset';
const ACK_ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// ============================================
// VALID TABS FOR URL PERSISTENCE
// ============================================
const VALID_TABS = ['my-assets', 'transactions'];

// ============================================
// CATEGORY ICON COMPONENT
// ============================================
const CategoryIcon = ({ code, className = "w-5 h-5" }) => {
  const icons = {
    IT: Laptop,
    ACC: Key,
    FLT: Car,
    LIC: FileKey,
    OFF: Package,
    FUEL: CreditCard,
    DSL: Fuel,
    TEL: Smartphone,
    DOOR: DoorOpen,
  };
  const Icon = icons[code] || Package;
  return <Icon className={className} />;
};

// ============================================
// STAT CARD COMPONENT
// ============================================
const StatCard = ({ title, value, icon: Icon, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    purple: 'bg-purple-100 text-purple-600',
  };
  
  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
      <div className="flex items-center">
        <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${colorClasses[color]}`}>
          <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
        </div>
        <div className="ml-2 sm:ml-4 min-w-0">
          <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-xs sm:text-sm text-gray-600 truncate">{title}</p>
        </div>
      </div>
    </div>
  );
};

/** Driver-facing badge label for monthly inspection row_status. */
const INSPECTION_STATUS_LABELS = {
  done: 'Completed',
  flagged: 'Issues reported',
  due: 'Due now',
  overdue: 'Overdue',
};

/**
 * One-line explanation under the monthly-inspection badge.
 * Speaks the driver's language (submit / clear / custodian), not ops jargon.
 */
const inspectionStatusHint = (row) => {
  if (!row) return '';
  const when = row.last_inspection_date ? formatDate(row.last_inspection_date) : null;
  switch (row.row_status) {
    case 'done':
      return when
        ? `Submitted ${when}. Clear for this month.`
        : 'Submitted. Clear for this month.';
    case 'flagged':
      return when
        ? `Submitted ${when}. Custodian was notified.`
        : 'Submitted. Custodian was notified.';
    case 'overdue':
      return when
        ? `Last submitted ${when}. Complete this month's check.`
        : 'Not submitted this month. Complete when you see the vehicle.';
    case 'due':
      return when
        ? `Last submitted ${when}. Due again this month.`
        : 'Not submitted yet this month.';
    default:
      return when ? `Last submitted ${when}` : 'No inspection on file yet.';
  }
};

// ============================================
// ASSET CARD COMPONENT
// ============================================
const AssetCard = ({
  asset, onTransfer, onViewDetails, onReturn, onViewLetter,
  onSendToWorkshop, onReturnFromWorkshop, inspectionRow, onInspect,
  onReportAccident,
}) => {
  const categoryCode = asset.asset_type?.category?.code;
  const colors = getCategoryColorByCode(categoryCode);
  const hasLetter = Boolean(asset.ack_letter_id);
  const isFleet = categoryCode === 'FLT';
  const isAtWorkshop = isFleet && asset.status === 'at_workshop';
  const inspectionActionable = isFleet && !isAtWorkshop
    && (inspectionRow?.row_status === 'due' || inspectionRow?.row_status === 'overdue');
  const isInspectionOverdue = inspectionRow?.row_status === 'overdue';

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onViewDetails(asset);
    }
  };

  const stopCardOpen = (e) => {
    e.stopPropagation();
  };

  return (
    <div
      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow flex flex-col h-full gap-3 cursor-pointer"
      role="button"
      tabIndex={0}
      onClick={() => onViewDetails(asset)}
      onKeyDown={handleCardKeyDown}
      aria-label={`View details for ${assetDisplayName(asset)}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-10 h-10 ${colors.bg} rounded-full flex items-center justify-center flex-shrink-0`}>
            <CategoryIcon code={categoryCode} className={`w-5 h-5 ${colors.text}`} />
          </div>
          <div className="min-w-0">
            <h4 className="font-semibold text-gray-900 flex items-center gap-1.5">
              <span className="truncate">{assetDisplayName(asset)}</span>
              {isDeviceMobileLine(asset) && (
                <InfoBadge color="blue" size="xs" label="Device" title={deviceLineLabel(asset)} />
              )}
            </h4>
            <p className="text-sm text-gray-600">{asset.asset_type?.name}</p>
            {isDeviceMobileLine(asset) && (
              <p className="text-xs text-blue-700 mt-1">{deviceLineLabel(asset)}</p>
            )}
            {asset.brand && asset.model && (
              <p className="text-xs text-gray-500 mt-1">
                {asset.model.toLowerCase().startsWith(asset.brand.toLowerCase())
                  ? asset.model
                  : `${asset.brand} ${asset.model}`}
              </p>
            )}
          </div>
        </div>
        <div className="flex-shrink-0">
          <StatusBadge status={asset.status} />
        </div>
      </div>

      {/* Left margin = icon width (40px) + gap (12px) = 52px to align with text */}
      <div className="ml-[52px] space-y-1 flex-1">
        {asset.serial_number && (
          <p className="text-xs text-gray-500">
            S/N: {asset.serial_number}
          </p>
        )}

        {/* Show activation_date for door access (ACC), assigned_date for others */}
        {(categoryCode === 'ACC' && asset.activation_date) ? (
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Registered: {formatDate(asset.activation_date)}
          </p>
        ) : asset.assigned_date && (
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            Assigned: {formatDate(asset.assigned_date)}
          </p>
        )}

        {/* Monthly inspection status (Fleet only).
            Drivers think in "monthly inspection" (modal title), not ops jargon
            like "walk-around" / bare "Done". Name the task, then the outcome. */}
        {isFleet && inspectionRow && (
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-2.5 py-2 space-y-1">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs font-medium text-gray-700 shrink-0">
                Monthly inspection
              </span>
              <StatusBadge
                status={inspectionRow.row_status}
                label={INSPECTION_STATUS_LABELS[inspectionRow.row_status]}
              />
            </div>
            <p className="text-xs text-gray-500">
              {inspectionStatusHint(inspectionRow)}
            </p>
          </div>
        )}
      </div>

      {/* Compliance primary CTA — labelled, not buried among utility icons */}
      {inspectionActionable && (
        <div onClick={stopCardOpen} onKeyDown={stopCardOpen}>
          <button
            type="button"
            onClick={() => onInspect(asset, inspectionRow)}
            className={
              isInspectionOverdue
                ? 'btn btn-danger w-full'
                : 'btn btn-primary w-full'
            }
            aria-label={
              isInspectionOverdue
                ? `Inspection overdue for ${asset.asset_tag} — complete now`
                : `Complete monthly inspection for ${asset.asset_tag}`
            }
          >
            <ClipboardCheck className="w-4 h-4 mr-2" />
            {isInspectionOverdue ? 'Inspection Overdue — Complete Now' : 'Complete Monthly Inspection'}
          </button>
        </div>
      )}

      {/* Real actions only — View is whole-card tap (DS card action placement).
          Order (L→R): read-only → frequent ops → service → urgent → custody-ending.
          Keeps Return away from Workshop (both used to be adjacent amber/orange)
          and Accident on icon-button-danger (not a color-hacked ghost). */}
      <div className="card-action-footer flex-nowrap" onClick={stopCardOpen} onKeyDown={stopCardOpen}>
        {hasLetter && (
          <button
            type="button"
            onClick={() => onViewLetter(asset)}
            className="icon-button"
            title={`View acknowledgement letter${asset.ack_letter_reference ? ` (${asset.ack_letter_reference})` : ''}`}
            aria-label={`View acknowledgement letter${asset.ack_letter_reference ? ` (${asset.ack_letter_reference})` : ''}`}
          >
            <FileText className="w-4 h-4" />
          </button>
        )}
        {isAtWorkshop ? (
          /* Vehicle is currently at a workshop: only allow closing the loop. */
          <button
            type="button"
            onClick={() => onReturnFromWorkshop(asset)}
            className="icon-button text-green-600 hover:text-green-700 hover:bg-green-50"
            title="Return from Workshop"
            aria-label="Return from Workshop"
          >
            <Check className="w-4 h-4" />
          </button>
        ) : (
          <>
            {asset.asset_type?.transfer_policy === 'peer_transfer' && (
              <button
                type="button"
                onClick={() => onTransfer(asset)}
                className="icon-button"
                title="Transfer Asset"
                aria-label="Transfer Asset"
              >
                <ArrowLeftRight className="w-4 h-4" />
              </button>
            )}
            {isFleet && (
              <button
                type="button"
                onClick={() => onSendToWorkshop(asset)}
                className="icon-button text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                title="Send to Workshop"
                aria-label="Send to Workshop"
              >
                <Wrench className="w-4 h-4" />
              </button>
            )}
            {isFleet && onReportAccident && (
              <button
                type="button"
                onClick={() => onReportAccident(asset)}
                className="icon-button-danger"
                title="Report Accident"
                aria-label="Report Accident"
              >
                <Siren className="w-4 h-4" />
              </button>
            )}
            {canReturnAssetToInventory(asset) && (
              <button
                type="button"
                onClick={() => onReturn(asset)}
                className="icon-button text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                title="Return to Inventory"
                aria-label="Return to Inventory"
              >
                <CornerDownLeft className="w-4 h-4" />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ============================================
// LETTER PREVIEW MODAL
// ============================================
// LetterPreviewModal is now a shared component at frontend/src/components/LetterPreviewModal.js

// ============================================
// PENDING TRANSFER CARD (INCOMING)
// ============================================
const IncomingTransferCard = ({ transfer, onAccept, onReject }) => {
  const expiresAt = transfer.expires_at ? new Date(transfer.expires_at) : null;
  const isExpiringSoon = expiresAt && (expiresAt - new Date()) < 24 * 60 * 60 * 1000; // < 24 hours
  
  return (
    <div className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow flex flex-col h-full gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Inbox className="w-5 h-5 text-yellow-600" />
          </div>
          <div className="min-w-0">
            <h4 className="font-semibold text-gray-900">{transfer.asset?.asset_tag}</h4>
            <p className="text-sm text-gray-600">{transfer.asset?.asset_type?.name}</p>
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <User className="w-3 h-3" />
              From: {transfer.from_employee?.first_name} {transfer.from_employee?.last_name}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <StatusBadge status={transfer.status} />
        </div>
      </div>

      <div className="space-y-2 flex-1">
        {transfer.notes && (
          <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-2">
            "{transfer.notes}"
          </p>
        )}

        {expiresAt && (
          <p className={`text-xs flex items-center gap-1 ${isExpiringSoon ? 'text-red-600' : 'text-gray-500'}`}>
            <Clock className="w-3 h-3" />
            Expires: {formatDate(expiresAt)}
            {isExpiringSoon && <span className="font-medium">(Expiring soon!)</span>}
          </p>
        )}
      </div>

      <div className="card-action-footer flex-nowrap">
        <button
          type="button"
          onClick={() => onReject(transfer)}
          title="Reject"
          aria-label="Reject"
          className="icon-button-danger"
        >
          <XCircle className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={() => onAccept(transfer)}
          title="Accept"
          aria-label="Accept"
          className="p-2 rounded-full bg-blue-800 text-white hover:bg-blue-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
          <CheckCircle className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// ============================================
// PENDING TRANSFER CARD (OUTGOING)
// ============================================
const OutgoingTransferCard = ({ transfer, onCancel }) => {
  const expiresAt = transfer.expires_at ? new Date(transfer.expires_at) : null;
  
  return (
    <div className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow flex flex-col h-full gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Send className="w-5 h-5 text-blue-800" />
          </div>
          <div className="min-w-0">
            <h4 className="font-semibold text-gray-900">{transfer.asset?.asset_tag}</h4>
            <p className="text-sm text-gray-600">{transfer.asset?.asset_type?.name}</p>
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <User className="w-3 h-3" />
              To: {transfer.to_employee?.first_name} {transfer.to_employee?.last_name}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <StatusBadge status={transfer.status} />
        </div>
      </div>

      <div className="flex-1">
        {expiresAt && (
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Expires: {formatDate(expiresAt)}
          </p>
        )}
      </div>

      <div className="card-action-footer flex-nowrap">
        <button
          type="button"
          onClick={() => onCancel(transfer)}
          className="btn btn-sm btn-outline text-xs flex gap-1"
        >
          <X className="w-3 h-3" />
          Cancel Transfer
        </button>
      </div>
    </div>
  );
};

// ============================================
// PENDING RECEIPT CARD
// ============================================
const PendingReceiptCard = ({ transfer, onAcknowledge, onDecline }) => {
  const expiresAt = transfer.expires_at ? new Date(transfer.expires_at) : null;
  const isExpiringSoon = expiresAt && (expiresAt - new Date()) < 24 * 60 * 60 * 1000;
  const isTransfer = !!transfer.from_employee_name;

  return (
    <div className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow flex flex-col h-full gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
            <ClipboardCheck className="w-5 h-5 text-amber-600" />
          </div>
          <div className="min-w-0">
            <h4 className="font-semibold text-gray-900">{transfer.asset_tag || transfer.asset?.asset_tag}</h4>
            <p className="text-sm text-gray-600">{transfer.asset_type_name || transfer.asset?.asset_type?.name}</p>
            <p className="text-xs text-gray-500 mt-1">
              {isTransfer
                ? `Transferred to you from ${transfer.from_employee_name}`
                : `Assigned by: ${transfer.created_by_name || 'Custodian'}`}
              {isTransfer && transfer.created_by_name && (
                <span> (by {transfer.created_by_name})</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <StatusBadge status={transfer.status} />
        </div>
      </div>

      <div className="space-y-2 flex-1">
        <p className="text-sm text-amber-800 bg-amber-50 rounded-lg p-2">
          Please acknowledge receipt to add this asset to My Assets.
        </p>

        {expiresAt && (
          <p className={`text-xs flex items-center gap-1 ${isExpiringSoon ? 'text-red-600' : 'text-gray-500'}`}>
            <Clock className="w-3 h-3" />
            Expires: {formatDate(expiresAt)}
            {isExpiringSoon && <span className="font-medium">(Expiring soon!)</span>}
          </p>
        )}
      </div>

      <div className="card-action-footer flex-nowrap">
        <button
          type="button"
          onClick={() => onDecline(transfer)}
          title="Decline"
          aria-label="Decline"
          className="icon-button-danger"
        >
          <XCircle className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={() => onAcknowledge(transfer)}
          title="Acknowledge receipt"
          aria-label="Acknowledge receipt"
          className="p-2 rounded-full bg-blue-800 text-white hover:bg-blue-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
          <CheckCircle className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// ============================================
// TRANSFER MODAL
// ============================================
const TransferModal = ({ isOpen, onClose, asset, onSubmit }) => {
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  
  // Debounce search to avoid excessive API calls
  const debouncedSearch = useDebounce(search, 300);
  const shouldSearch = debouncedSearch.trim().length >= 2;
  
  const { 
    data: staffListData, 
    isLoading: loadingStaff,
    isError,
    error 
  } = useStaffList({ 
    search: shouldSearch ? debouncedSearch : undefined,
    page_size: 10 // Limit results from backend
  });
  
  const staffList = staffListData?.results || [];
  
  // Filter out current assignee (backend doesn't handle this exclusion)
  const filteredStaff = useMemo(() => {
    if (!shouldSearch) return [];
    return staffList.filter((s) =>
      s.staff_id !== asset?.current_assignee?.staff_id
      && (s.employment_status || '').toLowerCase() !== 'inactive'
    );
  }, [staffList, shouldSearch, asset?.current_assignee?.staff_id]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedEmployee) return;
    onSubmit({ toEmployeeId: selectedEmployee, notes });
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Transfer Asset"
      size="sm"
      theme="employee"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline">
            Cancel
          </button>
          <button
            type="submit"
            form="transfer-asset-form"
            disabled={!selectedEmployee}
            className="btn btn-primary"
          >
            <Send className="w-4 h-4 mr-2" />
            Request Transfer
          </button>
        </>
      }
    >
      <form id="transfer-asset-form" onSubmit={handleSubmit} className="space-y-4">
          {/* Asset Info */}
          <div className="bg-blue-50 rounded-lg p-3">
            <p className="text-sm font-medium text-blue-800">{asset?.asset_tag}</p>
            <p className="text-xs text-blue-600">{asset?.asset_type?.name}</p>
          </div>
          
          {/* Employee Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transfer to <span className="text-red-500">*</span>
            </label>
            <DebouncedSearchInput
              value={search}
              onSearch={(val) => {
                setSearch(val);
                setSelectedEmployee('');
              }}
              placeholder="Search employee by name or ID..."
              theme="employee"
            />
            
            {/* Search Results */}
            {search.trim() && (
              <div className="mt-2 border rounded-lg max-h-40 overflow-y-auto">
                {isError ? (
                  <div className="p-3 text-center text-sm text-red-600">
                    Failed to load employees. Please try again.
                  </div>
                ) : loadingStaff ? (
                  <div className="p-3 text-center text-sm text-gray-500">Searching...</div>
                ) : !shouldSearch ? (
                  <div className="p-3 text-center text-sm text-gray-500">
                    Type at least 2 characters to search
                  </div>
                ) : filteredStaff.length === 0 ? (
                  <div className="p-3 text-center text-sm text-gray-500">No employees found</div>
                ) : (
                  filteredStaff.map(emp => (
                    <button
                      key={emp.staff_id}
                      type="button"
                      onClick={() => {
                        setSelectedEmployee(emp.staff_id);
                        setSearch(`${emp.first_name} ${emp.last_name} (${emp.staff_id})`);
                      }}
                      className={`w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-2 ${
                        selectedEmployee === emp.staff_id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <User className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium">{emp.first_name} {emp.last_name}</p>
                        <p className="text-xs text-gray-500">{emp.staff_id} • {emp.department?.name}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          
          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add a message for the recipient..."
              className="input w-full"
            />
          </div>
          
          {/* Info */}
          <div className="bg-yellow-50 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-yellow-700">
              The recipient must accept this transfer within 7 days. 
              The asset will remain in your possession until they accept.
            </p>
          </div>
          
      </form>
    </Modal>
  );
};

// ============================================
// RETURN TO INVENTORY MODAL
// ============================================
const ReturnModal = ({ isOpen, onClose, asset, onSubmit, isLoading }) => {
  const [returnReason, setReturnReason] = useState('');
  const [notes, setNotes] = useState('');
  const isSoftware = isSoftwareLicenseAsset(asset);
  const isPhysical = requiresPhysicalHandover(asset);

  const returnReasons = isPhysical
    ? [
        { value: 'no_longer_needed', label: 'No Longer Needed' },
        { value: 'faulty', label: 'Faulty/Not Working' },
        { value: 'damaged', label: 'Damaged' },
        { value: 'upgrade_requested', label: 'Upgrade Requested' },
        { value: 'other', label: 'Other' },
      ]
    : [
        { value: 'no_longer_needed', label: 'No Longer Needed' },
        { value: 'upgrade_requested', label: isSoftware ? 'Replaced / Different Product' : 'Replaced' },
        { value: 'other', label: 'Other' },
      ];
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!returnReason) return;
    if (returnReason === 'other' && !notes.trim()) return;
    onSubmit({ returnReason, notes });
  };
  
  // Reset form when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setReturnReason('');
      setNotes('');
    }
  }, [isOpen]);
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Return to Inventory"
      size="sm"
      theme="employee"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline">
            Cancel
          </button>
          <button
            type="submit"
            form="return-inventory-form"
            disabled={!returnReason || isLoading || (returnReason === 'other' && !notes.trim())}
            className="btn bg-orange-600 text-white hover:bg-orange-700"
          >
            <CornerDownLeft className="w-4 h-4 mr-2" />
            {isLoading ? 'Submitting...' : 'Request Return'}
          </button>
        </>
      }
    >
      <form id="return-inventory-form" onSubmit={handleSubmit} className="space-y-4">
            {/* Asset Info */}
            <div className="bg-orange-50 rounded-lg p-3">
              <p className="text-sm font-medium text-orange-800">{asset?.asset_tag}</p>
              <p className="text-xs text-orange-600">{asset?.asset_type?.name}</p>
            </div>
            
            {/* Return Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Return <span className="text-red-500">*</span>
              </label>
              <FormSelect
                value={returnReason}
                onChange={setReturnReason}
                options={[
                  { value: '', label: 'Select a reason...' },
                  ...returnReasons
                ]}
                theme="employee"
                required
              />
            </div>
            
            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes {returnReason === 'other' && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={returnReason === 'other' 
                  ? "Please describe the reason..." 
                  : "Add any additional details (optional)..."}
                className="input w-full"
                required={returnReason === 'other'}
              />
              {returnReason === 'other' && !notes.trim() && (
                <p className="text-xs text-red-500 mt-1">Notes are required when selecting "Other"</p>
              )}
            </div>
            
            {/* Info */}
            <div className="bg-yellow-50 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-700">
                {isPhysical
                  ? 'A custodian will need to confirm receipt of this asset. The return request will expire if not confirmed within 7 days.'
                  : isSoftware
                    ? 'A custodian will confirm this seat has been reclaimed. The request expires in 7 days if not confirmed.'
                    : 'A custodian will confirm this entitlement has been reclaimed. The request expires in 7 days if not confirmed.'}
              </p>
            </div>
            
      </form>
    </Modal>
  );
};

// ============================================
// SEND TO WORKSHOP MODAL
// ============================================
const SendToWorkshopModal = ({ isOpen, onClose, asset, onSubmit, isLoading }) => {
  const [workshopId, setWorkshopId] = useState('');
  const [signerName, setSignerName] = useState('');
  const [notes, setNotes] = useState('');
  const [hasSignature, setHasSignature] = useState(false);
  const signatureRef = useRef(null);

  const { data: workshops = [], isLoading: loadingWorkshops } = useWorkshops();

  const selectedWorkshop = workshops.find((w) => String(w.id) === String(workshopId));
  const reps = (selectedWorkshop?.contacts || [])
    .filter((c) => c.is_active)
    .sort((a, b) => (b.times_seen || 0) - (a.times_seen || 0));

  useEffect(() => {
    if (!isOpen) {
      setWorkshopId('');
      setSignerName('');
      setNotes('');
      setHasSignature(false);
      signatureRef.current?.clear?.();
    }
  }, [isOpen]);

  // Reps are workshop-specific — clear the chosen rep when the workshop changes.
  useEffect(() => {
    setSignerName('');
  }, [workshopId]);

  const canSubmit = workshopId && signerName.trim() && hasSignature && !isLoading;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      workshopId,
      signerName: signerName.trim(),
      signatureBase64: signatureRef.current?.toDataURL?.() || '',
      notes,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Send to Workshop"
      size="sm"
      theme="employee"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline">
            Cancel
          </button>
          <button
            type="submit"
            form="send-workshop-form"
            disabled={!canSubmit}
            className="btn bg-amber-600 text-white hover:bg-amber-700"
          >
            <Wrench className="w-4 h-4 mr-2" />
            {isLoading ? 'Sending...' : 'Send to Workshop'}
          </button>
        </>
      }
    >
      <form id="send-workshop-form" onSubmit={handleSubmit} className="space-y-4">
              {/* Asset Info */}
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-sm font-medium text-amber-800">{asset?.asset_tag}</p>
                <p className="text-xs text-amber-600">{asset?.asset_type?.name}</p>
              </div>

              {/* Workshop */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Workshop <span className="text-red-500">*</span>
                </label>
                <FormSelect
                  value={workshopId}
                  onChange={setWorkshopId}
                  options={[
                    { value: '', label: loadingWorkshops ? 'Loading workshops...' : 'Select a workshop...' },
                    ...workshops.map((w) => ({ value: String(w.id), label: w.name })),
                  ]}
                  theme="employee"
                  required
                />
              </div>

              {/* Workshop rep — pick a remembered rep or add a new one (creatable). */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Received by (workshop representative) <span className="text-red-500">*</span>
                </label>
                <SearchableCombobox
                  items={reps}
                  value={signerName}
                  displayValue={signerName}
                  onChange={setSignerName}
                  displayFn={(c) => c.name}
                  valueFn={(c) => c.name}
                  searchFn={(c, t) => c.name.toLowerCase().includes(t)}
                  placeholder={workshopId ? 'Select or add a rep…' : 'Select a workshop first'}
                  searchPlaceholder="Search or type a new name…"
                  emptyLabel="No saved reps — type to add one"
                  variant="form"
                  theme="employee"
                  disabled={!workshopId}
                  creatableLabel={(t) => (t.trim() ? `Add "${t.trim()}"` : 'Type a name to add')}
                  onCreateNew={(t) => t.trim() && setSignerName(t.trim())}
                />
                <p className="mt-1 text-xs text-gray-500">
                  New names are saved to this workshop for next time.
                </p>
              </div>

              {/* Signature */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Signature <span className="text-red-500">*</span>
                </label>
                <SignatureCanvas ref={signatureRef} onChange={setHasSignature} />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Reason for service / remarks..."
                  className="input w-full"
                />
              </div>

              <div className="bg-yellow-50 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-700">
                  The vehicle status will change to <strong>At Workshop</strong>. Upload the
                  workshop's service chit when it returns to close the loop.
                </p>
              </div>
      </form>
    </Modal>
  );
};

// ============================================
// RETURN FROM WORKSHOP MODAL
// ============================================
const RETURN_CHIT_MAX_BYTES = 10 * 1024 * 1024;
const RETURN_CHIT_ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];

const ReturnFromWorkshopModal = ({ isOpen, onClose, asset, onSubmit, isLoading }) => {
  const [file, setFile] = useState(null);
  const [selectedParts, setSelectedParts] = useState([]);
  const [notes, setNotes] = useState('');
  const fileInputRef = useRef(null);

  const { data: serviceParts = [] } = useServiceParts();

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setSelectedParts([]);
      setNotes('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!RETURN_CHIT_ALLOWED_TYPES.includes(f.type)) {
      toast.error('Only JPEG, PNG, WebP, or PDF files are allowed.');
      return;
    }
    if (f.size > RETURN_CHIT_MAX_BYTES) {
      toast.error('The service chit must be 10MB or less.');
      return;
    }
    setFile(f);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file || isLoading) return;
    onSubmit({ serviceChit: file, partsServiced: selectedParts, notes });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Return from Workshop"
      size="sm"
      theme="employee"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline">
            Cancel
          </button>
          <button
            type="submit"
            form="return-workshop-form"
            disabled={!file || isLoading}
            className="btn bg-green-600 text-white hover:bg-green-700"
          >
            <Check className="w-4 h-4 mr-2" />
            {isLoading ? 'Submitting...' : 'Mark Returned'}
          </button>
        </>
      }
    >
      <form id="return-workshop-form" onSubmit={handleSubmit} className="space-y-4">
              {/* Asset Info */}
              <div className="bg-green-50 rounded-lg p-3">
                <p className="text-sm font-medium text-green-800">{asset?.asset_tag}</p>
                <p className="text-xs text-green-600">{asset?.asset_type?.name}</p>
              </div>

              {/* Service chit upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Service Chit <span className="text-red-500">*</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="service-chit-input"
                />
                <label
                  htmlFor="service-chit-input"
                  className="flex items-center justify-center gap-2 w-full border-2 border-dashed border-gray-300 rounded-lg p-4 cursor-pointer hover:border-green-400 hover:bg-green-50 transition-colors"
                >
                  <Upload className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {file ? file.name : 'Tap to upload photo or PDF of the chit'}
                  </span>
                </label>
              </div>

              {/* What was done — multi-select from the admin-managed parts catalog. */}
              {serviceParts.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    What was done (select all that apply)
                  </label>
                  <SearchableMultiCombobox
                    items={serviceParts}
                    value={selectedParts}
                    onChange={setSelectedParts}
                    displayFn={(p) => p.name}
                    valueFn={(p) => p.code}
                    searchFn={(p, t) => p.name.toLowerCase().includes(t)}
                    placeholder="Select parts / services…"
                    searchPlaceholder="Search parts…"
                    variant="form"
                    theme="employee"
                    showChips
                    className="w-full"
                  />
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optional)
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Remarks about the service..."
                  className="input w-full"
                />
              </div>

              <div className="bg-yellow-50 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-700">
                  Uploading the chit returns the vehicle to <strong>Assigned</strong>. Select what
                  was done so the service history stays accurate.
                </p>
              </div>
      </form>
    </Modal>
  );
};

// ============================================
// PENDING RETURN CARD (FOR EMPLOYEE)
// ============================================
const PendingReturnCard = ({ returnTransfer, onCancel }) => {
  const expiresAt = returnTransfer.expires_at ? new Date(returnTransfer.expires_at) : null;
  const isExpiringSoon = expiresAt && (expiresAt - new Date()) < 24 * 60 * 60 * 1000;
  
  const reasonLabels = {
    no_longer_needed: 'No Longer Needed',
    faulty: 'Faulty/Not Working',
    damaged: 'Damaged',
    upgrade_requested: 'Upgrade Requested',
    other: 'Other',
  };
  
  return (
    <div className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow flex flex-col h-full gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
            <RotateCcw className="w-5 h-5 text-orange-600" />
          </div>
          <div className="min-w-0">
            <h4 className="font-semibold text-gray-900">{returnTransfer.asset?.asset_tag}</h4>
            <p className="text-sm text-gray-600">{returnTransfer.asset?.asset_type?.name}</p>
            <p className="text-xs text-gray-500 mt-1">
              Reason: {reasonLabels[returnTransfer.return_reason] || returnTransfer.return_reason}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <StatusBadge status={returnTransfer.status} />
        </div>
      </div>

      <div className="space-y-2 flex-1">
        {returnTransfer.notes && (
          <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-2">
            "{returnTransfer.notes}"
          </p>
        )}

        {expiresAt && (
          <p className={`text-xs flex items-center gap-1 ${isExpiringSoon ? 'text-red-600' : 'text-gray-500'}`}>
            <Clock className="w-3 h-3" />
            Expires: {formatDate(expiresAt)}
            {isExpiringSoon && <span className="font-medium">(Expiring soon!)</span>}
          </p>
        )}
      </div>

      <div className="card-action-footer flex-nowrap">
        <button
          type="button"
          onClick={() => onCancel(returnTransfer)}
          className="btn btn-sm btn-outline text-xs flex gap-1"
        >
          <X className="w-3 h-3" />
          Cancel Return
        </button>
      </div>
    </div>
  );
};

// ============================================
// HELPER: history summary line for HistoryTransactionCard
// ============================================
const getHistorySummaryLine = (t, me) => {
  const status = t.status;
  const type = t.transfer_type;
  const fromId = t.from_employee?.staff_id ?? t.from_employee_staff_id;
  const toId = t.to_employee?.staff_id ?? t.to_employee_staff_id;
  const isFrom = fromId === me;
  const isTo = toId === me;
  const fromName = t.from_employee_name || (t.from_employee ? [t.from_employee.first_name, t.from_employee.last_name].filter(Boolean).join(' ') : '') || '—';
  const toName = t.to_employee_name || (t.to_employee ? [t.to_employee.first_name, t.to_employee.last_name].filter(Boolean).join(' ') : '') || '—';
  const returnReason = t.return_reason_display || t.return_reason || '—';
  if (status === 'completed') {
    if (type === 'return') return `Returned to inventory · ${returnReason}`;
    if (type === 'offboarding') return 'Collected during offboarding';
    if (type === 'initial') return 'Assigned to you';
    if (type === 'transfer' && isTo) return `Accepted from ${fromName}`;
    if (type === 'transfer' && isFrom) return `Transferred to ${toName}`;
  }
  if (status === 'rejected') {
    if (type === 'return') return 'Custodian declined your return';
    if (isTo) return `You rejected · From ${fromName}`;
    if (isFrom) return `${toName} declined`;
  }
  if (status === 'cancelled') {
    if (type === 'return') return 'Return request cancelled by you';
    if (isFrom) return `Transfer to ${toName} was cancelled`;
    if (isTo) return `Transfer from ${fromName} was cancelled`;
  }
  const typeLabel = resolveTransferTypeLabel(t) || type;
  const statusLabel = t.status_display || status;
  if (statusLabel && typeLabel) return `${statusLabel} · ${typeLabel}`;
  return statusLabel || typeLabel || '';
};

// ============================================
// HISTORY TRANSACTION CARD (Past transactions)
// ============================================
const HistoryTransactionCard = ({ transfer, currentUserStaffId, onViewDetails }) => {
  const line = getHistorySummaryLine(transfer, currentUserStaffId);
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onViewDetails?.(transfer);
    }
  };
  
  return (
    <div 
      className="p-4 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer"
      role="button"
      tabIndex={0}
      onClick={() => onViewDetails?.(transfer)}
      onKeyDown={handleKeyDown}
      aria-label={`View details for transaction ${transfer.transfer_reference}`}
    >
      {/* Mobile: Stack layout */}
      <div className="flex flex-col md:hidden gap-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-gray-500">{formatDate(transfer.effective_date || transfer.transfer_date)}</span>
          <StatusBadge status={transfer.status} label={transfer.status_display} />
        </div>
        <div className="min-w-0">
          <p className="font-medium text-gray-900 truncate">{transfer.asset_tag} · {transfer.asset_type_name}</p>
          <p className="text-xs text-gray-600 mt-0.5">{line}</p>
        </div>
      </div>
      
      {/* Desktop: Horizontal layout — items-start pins StatusBadge to title baseline */}
      <div className="hidden md:flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <span className="text-sm text-gray-500 w-24 flex-shrink-0">{formatDate(transfer.effective_date || transfer.transfer_date)}</span>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-900 truncate">{transfer.asset_tag} · {transfer.asset_type_name}</p>
            <p className="text-xs text-gray-600">{line}</p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <StatusBadge status={transfer.status} label={transfer.status_display} />
        </div>
      </div>
    </div>
  );
};

// ============================================
// SKELETON LOADING COMPONENT
// ============================================
const MyAssetsSkeleton = () => (
  <div className="space-y-6 bg-gray-50 min-h-screen">
    {/* Header Skeleton */}
    <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
      <div className="flex items-center gap-4 animate-pulse">
        <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
        <div className="space-y-2">
          <div className="h-6 bg-gray-200 rounded w-32"></div>
          <div className="h-4 bg-gray-200 rounded w-48"></div>
        </div>
      </div>
    </div>

    {/* Stats Cards Skeleton */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
            <div className="ml-2 sm:ml-4 space-y-2">
              <div className="h-8 bg-gray-200 rounded w-12"></div>
              <div className="h-4 bg-gray-200 rounded w-24"></div>
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Tabs Skeleton */}
    <div className="flex items-center gap-2 flex-wrap mb-4 md:mb-6">
      {[1, 2, 3].map(i => (
        <div key={i} className="h-10 bg-gray-200 rounded-full w-28 animate-pulse"></div>
      ))}
    </div>

    {/* Content Area Skeleton */}
    <div className="bg-white rounded-2xl shadow-sm border p-6">
      {/* Category Header Skeleton */}
      <div className="flex items-center gap-2 mb-4 animate-pulse">
        <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
        <div className="h-4 bg-gray-200 rounded w-24"></div>
        <div className="h-5 bg-gray-200 rounded-full w-6"></div>
      </div>
      
      {/* Asset Cards Skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white rounded-xl border p-4 animate-pulse">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                <div className="space-y-2">
                  <div className="h-5 bg-gray-200 rounded w-24"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                  <div className="h-3 bg-gray-200 rounded w-28 mt-1"></div>
                </div>
              </div>
              <div className="h-6 bg-gray-200 rounded-full w-16"></div>
            </div>
            <div className="mt-4 pt-3 border-t flex justify-end gap-2">
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ============================================
// ACCEPT TRANSFER MODAL
// ============================================
const AcceptModal = ({ isOpen, onClose, transfer, onSubmit, isLoading }) => {
  const [condition, setCondition] = useState('good');
  const [photos, setPhotos] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        (window.innerWidth <= 768);
      setIsMobile(isMobileDevice);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setCondition('good');
      setPhotos([]);
    }
  }, [isOpen]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setIsDragging(true);
    } else if (e.type === 'dragleave') {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const raw = Array.from(e.dataTransfer?.files || []);
    const images = raw.filter((f) => ACK_ALLOWED_IMAGE_TYPES.includes(f.type));
    const newPhotos = [...photos, ...images].slice(0, ACK_MAX_PHOTOS);
    setPhotos(newPhotos);
    if (raw.length && !images.length) {
      toast.error('No image files (JPEG, PNG, WebP) found.');
    }
    if (images.length > ACK_MAX_PHOTOS - photos.length) {
      toast.error(`Only ${ACK_MAX_PHOTOS - photos.length} more photo(s) can be added.`);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const images = files.filter((f) => ACK_ALLOWED_IMAGE_TYPES.includes(f.type));
    const newPhotos = [...photos, ...images].slice(0, ACK_MAX_PHOTOS);
    setPhotos(newPhotos);
    if (images.length > ACK_MAX_PHOTOS - photos.length) {
      toast.error(`Only ${ACK_MAX_PHOTOS - photos.length} more photo(s) can be added.`);
    }
    e.target.value = '';
  };

  const handleCameraCapture = (e) => {
    handleFileChange(e);
  };

  const handleSubmit = () => {
    // Validate photos are required
    if (photos.length === 0) {
      toast.error('Please upload at least one photo.');
      return;
    }
    
    // Validate photo limits
    if (photos.length > ACK_MAX_PHOTOS) {
      toast.error('Maximum 5 photos.');
      return;
    }
    
    const over = photos.find((f) => f.size > ACK_MAX_PHOTO_BYTES);
    if (over) {
      toast.error('Each photo must be 5MB or less.');
      return;
    }
    
    const total = photos.reduce((s, f) => s + f.size, 0);
    if (total > ACK_MAX_PHOTOS_TOTAL_BYTES) {
      toast.error('Total size of photos must be 25MB or less.');
      return;
    }
    
    onSubmit({ condition, photos });
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Accept Transfer"
      size="sm"
      theme="employee"
      contentClassName="space-y-4"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            className="btn btn-primary"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            {isLoading ? 'Accepting...' : 'Accept Transfer'}
          </button>
        </>
      }
    >
            {/* Asset Info */}
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-sm font-medium text-blue-800">{transfer?.asset?.asset_tag}</p>
              <p className="text-xs text-blue-600">{transfer?.asset?.asset_type?.name}</p>
              <p className="text-xs text-blue-600 mt-1">
                From: {transfer?.from_employee?.first_name} {transfer?.from_employee?.last_name}
              </p>
            </div>
            
            {/* Condition */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Condition at Receipt
              </label>
              <FormSelect
                value={condition}
                onChange={setCondition}
                options={[
                  { value: 'new', label: 'New' },
                  { value: 'good', label: 'Good' },
                  { value: 'fair', label: 'Fair' },
                  { value: 'poor', label: 'Poor' },
                  { value: 'faulty', label: 'Faulty' },
                ]}
                theme="employee"
              />
            </div>

            {/* Photos (required) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Photos <span className="text-red-500">*</span>
              </label>
              
              {/* Mobile: Show camera and gallery options separately */}
              {isMobile ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {/* Camera Input */}
                    <div>
                      <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        capture="environment"
                        multiple
                        className="hidden"
                        onChange={handleCameraCapture}
                      />
                      <button
                        type="button"
                        onClick={() => cameraInputRef.current?.click()}
                        className="w-full bg-white hover:bg-gray-50 text-gray-700 rounded-lg py-3 px-3 border-2 border-gray-300 hover:border-blue-400 hover:shadow-md transition-all flex flex-col items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      >
                        <Camera className="w-6 h-6 text-blue-600" />
                        <span className="text-xs font-medium">Take Photo</span>
                      </button>
                    </div>
                    
                    {/* Gallery Input */}
                    <div>
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        className="hidden"
                        onChange={handleFileChange}
                      />
                      <button
                        type="button"
                        onClick={() => photoInputRef.current?.click()}
                        className="w-full bg-white hover:bg-gray-50 text-gray-700 rounded-lg py-3 px-3 border-2 border-gray-300 hover:border-blue-400 hover:shadow-md transition-all flex flex-col items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                      >
                        <Upload className="w-6 h-6 text-blue-600" />
                        <span className="text-xs font-medium">From Gallery</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* Desktop: Traditional drag-and-drop */
                <div
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => photoInputRef.current?.click()}
                  className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer transition ${
                    isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <ImagePlus className="w-8 h-8 text-gray-400 mb-1.5" />
                  <p className="text-sm text-gray-600">Drag & drop or click to add photos</p>
                </div>
              )}
              
              <p className="text-xs text-gray-500 mt-1.5">
                {isMobile ? 'Take photo or upload from gallery. ' : ''}Up to 5 images, 5MB each. JPEG, PNG, WebP.
              </p>
              
              {/* Photo Previews */}
              {photos.length > 0 && (
                <div className="mt-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {photos.map((file, i) => {
                      const previewUrl = URL.createObjectURL(file);
                      return (
                        <div key={i} className="relative group">
                          <img
                            src={previewUrl}
                            alt={`Preview ${i + 1}`}
                            className="w-full h-24 object-cover rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={(ev) => {
                              ev.stopPropagation();
                              URL.revokeObjectURL(previewUrl);
                              setPhotos((p) => p.filter((_, idx) => idx !== i));
                            }}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="Remove photo"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <div className="absolute bottom-1 left-1 right-1 bg-black bg-opacity-50 text-white text-xs px-1 py-0.5 rounded truncate">
                            {file.name}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {photos.length < ACK_MAX_PHOTOS && (
                    <p className="text-xs text-gray-500 text-center">
                      {ACK_MAX_PHOTOS - photos.length} more photo(s) can be added
                    </p>
                  )}
                </div>
              )}
            </div>
            
    </Modal>
  );
};

// ============================================
// ACKNOWLEDGE RECEIPT MODAL (pending-receipt assign)
// ============================================
const AcknowledgeReceiptModal = ({ isOpen, onClose, transfer, onSubmit, isLoading, acknowledgementDeclaration }) => {
  const [agreed, setAgreed] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const photoInputRef = useRef(null);
  const isE2E = !!(transfer?.from_employee_id || transfer?.from_employee);
  const declaration = (acknowledgementDeclaration || '').trim() || 'I acknowledge that I have received the company asset(s) listed above and accept responsibility for their care, appropriate use, safekeeping, and return in good condition when required by the company, on transfer, or on separation.';

  useEffect(() => {
    if (isOpen) { setAgreed(false); setPhotos([]); }
  }, [isOpen]);

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setIsDragging(true);
    else if (e.type === 'dragleave') setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const raw = Array.from(e.dataTransfer?.files || []);
    const images = raw.filter((f) => ACK_ALLOWED_IMAGE_TYPES.includes(f.type));
    setPhotos(images.slice(0, ACK_MAX_PHOTOS));
    if (raw.length && !images.length) toast.error('No image files (JPEG, PNG, WebP) found.');
    if (images.length > ACK_MAX_PHOTOS) toast.error('Only the first 5 images were added.');
  };
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const images = files.filter((f) => ACK_ALLOWED_IMAGE_TYPES.includes(f.type));
    setPhotos(images.slice(0, ACK_MAX_PHOTOS));
    e.target.value = '';
  };

  const handleSubmit = () => {
    if (photos.length > 0) {
      if (photos.length > ACK_MAX_PHOTOS) { toast.error('Maximum 5 photos.'); return; }
      const over = photos.find((f) => f.size > ACK_MAX_PHOTO_BYTES);
      if (over) { toast.error('Each photo must be 5MB or less.'); return; }
      const total = photos.reduce((s, f) => s + f.size, 0);
      if (total > ACK_MAX_PHOTOS_TOTAL_BYTES) { toast.error('Total size of photos must be 25MB or less.'); return; }
    }
    onSubmit({ condition: '', photos: photos.length > 0 ? photos : undefined });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Acknowledge Receipt"
      size="sm"
      theme="employee"
      contentClassName="space-y-4"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline">Cancel</button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading || !agreed}
            className="btn btn-primary"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            {isLoading ? 'Acknowledging...' : 'Acknowledge Receipt'}
          </button>
        </>
      }
    >
            <div className="bg-amber-50 rounded-lg p-3">
              <p className="text-sm font-medium text-amber-800">{transfer?.asset_tag || transfer?.asset?.asset_tag}</p>
              <p className="text-xs text-amber-600">{transfer?.asset_type_name || transfer?.asset?.asset_type?.name}</p>
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="ack-declaration"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="ack-declaration" className="text-sm text-gray-700">
                {declaration}
              </label>
            </div>

            {isE2E && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Photos (optional, recommended for employee-to-employee transfer)</label>
                <div
                  onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
                  onClick={() => photoInputRef.current?.click()}
                  className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer transition ${isDragging ? 'border-amber-400 bg-amber-50' : 'border-gray-300 hover:bg-gray-50'}`}
                >
                  <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={handleFileChange} />
                  <ImagePlus className="w-8 h-8 text-gray-400 mb-1.5" />
                  <p className="text-sm text-gray-600">Drag & drop or click to add photos</p>
                </div>
                <p className="text-xs text-gray-500 mt-1.5">Up to 5 images, 5MB each. JPEG, PNG, WebP.</p>
                {photos.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {photos.map((f, i) => (
                      <li key={i} className="flex items-center justify-between text-sm text-gray-600 bg-gray-50 rounded px-2 py-1">
                        <span className="truncate flex-1">{f.name}</span>
                        <button type="button" onClick={(ev) => { ev.stopPropagation(); setPhotos((p) => p.filter((_, idx) => idx !== i)); }} className="p-1 hover:bg-gray-200 rounded" aria-label="Remove"><X className="w-3 h-3" /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

    </Modal>
  );
};

// ============================================
// BATCH PENDING RECEIPT CARD
// ============================================
const BatchPendingReceiptCard = ({ transfers, onAcknowledge }) => {
  const count = transfers.length;
  const earliestExpiry = transfers
    .filter((t) => t.expires_at)
    .map((t) => new Date(t.expires_at))
    .sort((a, b) => a - b)[0];
  const isExpiringSoon = earliestExpiry && (earliestExpiry - new Date()) < 24 * 60 * 60 * 1000;
  const custodianName = transfers[0]?.created_by_name || 'Custodian';

  return (
    <div className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow flex flex-col h-full gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
            <ClipboardCheck className="w-5 h-5 text-amber-600" />
          </div>
          <div className="min-w-0">
            <h4 className="font-semibold text-gray-900">{count} Assets Pending</h4>
            <p className="text-sm text-gray-600">Batch assignment from {custodianName}</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {transfers.slice(0, 3).map((t) => (
                <span key={t.id} className="inline-block px-1.5 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                  {t.asset_tag || t.asset?.asset_tag}
                </span>
              ))}
              {count > 3 && (
                <span className="inline-block px-1.5 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                  +{count - 3} more
                </span>
              )}
            </div>
          </div>
        </div>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 flex-shrink-0">
          Pending
        </span>
      </div>

      <div className="space-y-2 flex-1">
        <p className="text-sm text-amber-800 bg-amber-50 rounded-lg p-2">
          Please acknowledge receipt of all {count} items.
        </p>

        {earliestExpiry && (
          <p className={`text-xs flex items-center gap-1 ${isExpiringSoon ? 'text-red-600' : 'text-gray-500'}`}>
            <Clock className="w-3 h-3" />
            Earliest expiry: {formatDate(earliestExpiry)}
            {isExpiringSoon && <span className="font-medium">(Expiring soon!)</span>}
          </p>
        )}
      </div>

      <div className="card-action-footer flex-nowrap">
        <button
          type="button"
          onClick={() => onAcknowledge(transfers)}
          title="Acknowledge all"
          aria-label="Acknowledge all"
          className="p-2 rounded-full bg-blue-800 text-white hover:bg-blue-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        >
          <CheckCircle className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// ============================================
// BATCH ACKNOWLEDGE RECEIPT MODAL
// ============================================
const BatchAcknowledgeReceiptModal = ({ isOpen, onClose, transfers, onSubmit, isLoading, acknowledgementDeclaration }) => {
  const [agreed, setAgreed] = useState(false);
  const declaration = (acknowledgementDeclaration || '').trim() || 'I acknowledge that I have received the company asset(s) listed above and accept responsibility for their care, appropriate use, safekeeping, and return in good condition when required by the company, on transfer, or on separation.';

  useEffect(() => {
    if (isOpen) setAgreed(false);
  }, [isOpen]);

  const handleSubmit = () => {
    onSubmit({ condition: '' });
  };

  if (!transfers || transfers.length === 0) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Acknowledge Receipt (${transfers.length} items)`}
      size="sm"
      theme="employee"
      contentClassName="space-y-4"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline">Cancel</button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading || !agreed}
            className="btn btn-primary"
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            {isLoading ? 'Acknowledging...' : `Acknowledge All (${transfers.length})`}
          </button>
        </>
      }
    >
            <div className="bg-amber-50 rounded-lg p-3 space-y-1 max-h-48 overflow-y-auto">
              {transfers.map((t) => (
                <div key={t.id} className="flex items-center justify-between text-sm">
                  <span className="font-medium text-amber-800">{t.asset_tag || t.asset?.asset_tag}</span>
                  <span className="text-amber-600 text-xs">{t.asset_type_name || t.asset?.asset_type?.name}</span>
                </div>
              ))}
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="batch-ack-declaration"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="batch-ack-declaration" className="text-sm text-gray-700">
                {declaration}
              </label>
            </div>

    </Modal>
  );
};

// ============================================
// DECLINE MODAL (reject/decline transfer or assign)
// ============================================
const RejectModal = ({ isOpen, onClose, transfer, onSubmit, isLoading }) => {
  const [reason, setReason] = useState('');
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Decline"
      size="sm"
      theme="employee"
      contentClassName="space-y-4"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-outline">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSubmit({ reason })}
            disabled={isLoading || !reason.trim()}
            className="btn btn-danger"
          >
            <XCircle className="w-4 h-4 mr-2" />
            {isLoading ? 'Declining...' : 'Decline'}
          </button>
        </>
      }
    >
            <div className="bg-red-50 rounded-lg p-3">
              <p className="text-sm font-medium text-red-800">{transfer?.asset_tag || transfer?.asset?.asset_tag}</p>
              <p className="text-xs text-red-600">{transfer?.asset_type_name || transfer?.asset?.asset_type?.name}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Please provide a reason..."
                className="input w-full h-24 resize-none"
                required
              />
            </div>
            
    </Modal>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const MyAssets = () => {
  // Tab persistence
  const [activeTab, setActiveTab] = useTabPersistence('my-assets', 'my-assets', VALID_TABS);
  
  // Data fetching
  const { data: summary, isLoading, error } = useMyAssetsSummary();
  const { user } = useAuth();
  const { items: historyItems, hasNextPage: historyHasNext, isLoading: historyLoading, fetchNextPage: historyFetchNext } = useTransactionHistory(20);
  // Pending from summary (avoids 4 extra requests)
  const incomingTransfers = summary?.pending_incoming || [];
  const outgoingTransfers = summary?.pending_outgoing || [];
  const pendingReceiptTransfers = summary?.pending_receipt || [];
  const pendingReturns = summary?.pending_returns || [];
  const pendingCount = incomingTransfers.length + outgoingTransfers.length + pendingReceiptTransfers.length + pendingReturns.length;
  
  // Mutations
  const initiateTransfer = useInitiateTransfer();
  const acceptTransfer = useAcceptTransfer();
  const acknowledgeReceipt = useAcknowledgeReceipt();
  const rejectTransfer = useRejectTransfer();
  const cancelTransfer = useCancelTransfer();
  const initiateReturn = useInitiateReturn();
  const cancelReturn = useCancelReturn();
  const sendToWorkshop = useSendToWorkshop();
  const returnFromWorkshop = useReturnFromWorkshop();
  const submitInspection = useSubmitVehicleInspection();
  const { data: inspectionsDue = [] } = useMyInspectionsDue();

  // Map fleet asset_id -> this period's inspection status row (for AssetCard).
  // Coerce to Number so JSON number/string drift never hides the CTA.
  const inspectionsByAssetId = useMemo(
    () => Object.fromEntries(
      inspectionsDue.map((row) => [Number(row.asset_id), row])
    ),
    [inspectionsDue]
  );
  
  // Modal states
  const [transferModal, setTransferModal] = useState({ open: false, asset: null });
  const [returnModal, setReturnModal] = useState({ open: false, asset: null });
  const [sendToWorkshopModal, setSendToWorkshopModal] = useState({ open: false, asset: null });
  const [returnFromWorkshopModal, setReturnFromWorkshopModal] = useState({ open: false, asset: null });
  const [inspectModal, setInspectModal] = useState({ open: false, vehicle: null });
  const [reportAccidentModal, setReportAccidentModal] = useState({
    open: false, vehicleNumber: '',
  });
  const [acceptModal, setAcceptModal] = useState({ open: false, transfer: null });
  const [acknowledgeReceiptModal, setAcknowledgeReceiptModal] = useState({ open: false, transfer: null });
  const [batchAckModal, setBatchAckModal] = useState({ open: false, transfers: [] });
  const [rejectModal, setRejectModal] = useState({ open: false, transfer: null });
  const [detailModal, setDetailModal] = useState({ open: false, assetId: null });
  const [transferDetailModal, setTransferDetailModal] = useState({ open: false, transfer: null });
  const [confirmAction, setConfirmAction] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [letterPreviewModal, setLetterPreviewModal] = useState({ open: false, asset: null });

  const handleViewLetter = useCallback((asset) => {
    setLetterPreviewModal({ open: true, asset });
  }, []);
  
  // Tab definitions: My Assets + Transactions
  const tabs = [
    { id: 'my-assets', name: 'My Assets', icon: Package, badge: summary?.total_assets || 0 },
    { id: 'transactions', name: 'Transactions', icon: ArrowLeftRight, badge: pendingCount },
  ];

  const visibleTabs = ['my-assets', 'transactions'];
  const effectiveTab = visibleTabs.includes(activeTab) ? activeTab : 'my-assets';

  useEffect(() => {
    if (!visibleTabs.includes(activeTab)) setActiveTab('my-assets');
  }, [activeTab]);

  // Ordered list for "Needs your action": Incoming + Pending Receipt by expires_at; then Outgoing + Returns by date
  // Batched pending_receipt transfers (sharing a batch_id) are grouped into a single card.
  const actionItems = useMemo(() => {
    const standalone = pendingReceiptTransfers.filter((t) => !t.batch_id);
    const batched = pendingReceiptTransfers.filter((t) => t.batch_id);
    const batchGroups = {};
    batched.forEach((t) => {
      if (!batchGroups[t.batch_id]) batchGroups[t.batch_id] = [];
      batchGroups[t.batch_id].push(t);
    });

    const first = [
      ...incomingTransfers.map((t) => ({ t, type: 'incoming', sort: t.expires_at || 'Z' })),
      ...standalone.map((t) => ({ t, type: 'pending_receipt', sort: t.expires_at || 'Z' })),
      ...Object.entries(batchGroups).map(([batchId, transfers]) => ({
        t: transfers,
        type: 'batch_pending_receipt',
        sort: transfers[0]?.expires_at || 'Z',
        batchId,
      })),
    ].sort((a, b) => (a.sort || '').localeCompare(b.sort || ''));
    const second = [
      ...outgoingTransfers.map((t) => ({ t, type: 'outgoing', sort: t.effective_date || t.transfer_date || t.created_at || '' })),
      ...pendingReturns.map((t) => ({ t, type: 'return', sort: t.effective_date || t.transfer_date || t.created_at || '' })),
    ].sort((a, b) => (b.sort || '').localeCompare(a.sort || ''));
    return [...first, ...second];
  }, [incomingTransfers, pendingReceiptTransfers, outgoingTransfers, pendingReturns]);
  
  // Handlers
  const handleInitiateTransfer = (data) => {
    initiateTransfer.mutate({
      assetId: transferModal.asset.id,
      toEmployeeId: data.toEmployeeId,
      notes: data.notes,
    }, {
      onSuccess: () => setTransferModal({ open: false, asset: null }),
    });
  };
  
  const handleAcceptTransfer = (data) => {
    acceptTransfer.mutate({
      transferId: acceptModal.transfer.id,
      condition: data.condition,
      photos: data.photos,
    }, {
      onSuccess: () => setAcceptModal({ open: false, transfer: null }),
    });
  };
  
  const handleRejectTransfer = (data) => {
    rejectTransfer.mutate({
      transferId: rejectModal.transfer.id,
      reason: data.reason,
    }, {
      onSuccess: () => setRejectModal({ open: false, transfer: null }),
    });
  };

  const handleAcknowledgeReceipt = (data) => {
    acknowledgeReceipt.mutate({
      transferId: acknowledgeReceiptModal.transfer.id,
      condition: data.condition || '',
      photos: data.photos,
    }, {
      onSuccess: () => setAcknowledgeReceiptModal({ open: false, transfer: null }),
    });
  };

  const handleBatchAcknowledgeReceipt = (data) => {
    if (!batchAckModal.transfers?.length) return;
    acknowledgeReceipt.mutate({
      transferId: batchAckModal.transfers[0].id,
      condition: data.condition || '',
    }, {
      onSuccess: () => setBatchAckModal({ open: false, transfers: [] }),
    });
  };
  
  const handleCancelTransfer = (transfer) => {
    setConfirmAction({ type: 'cancelTransfer', transfer });
  };

  const handleCancelPendingReceipt = (transfer) => {
    setConfirmAction({ type: 'cancelPending', transfer });
  };
  
  const handleInitiateReturn = (data) => {
    initiateReturn.mutate({
      assetId: returnModal.asset.id,
      returnReason: data.returnReason,
      notes: data.notes,
    }, {
      onSuccess: () => setReturnModal({ open: false, asset: null }),
    });
  };
  
  const handleCancelReturn = (returnTransfer) => {
    setConfirmAction({ type: 'cancelReturn', transfer: returnTransfer });
  };

  const handleSendToWorkshop = (data) => {
    sendToWorkshop.mutate({
      assetId: sendToWorkshopModal.asset.id,
      ...data,
    }, {
      onSuccess: () => setSendToWorkshopModal({ open: false, asset: null }),
    });
  };

  const handleReturnFromWorkshop = (data) => {
    returnFromWorkshop.mutate({
      assetId: returnFromWorkshopModal.asset.id,
      ...data,
    }, {
      onSuccess: () => setReturnFromWorkshopModal({ open: false, asset: null }),
    });
  };

  const handleSubmitInspection = (data) => {
    submitInspection.mutate({
      assetId: inspectModal.vehicle.asset_id,
      ...data,
    }, {
      onSuccess: () => setInspectModal({ open: false, vehicle: null }),
    });
  };

  const handleConfirmAction = () => {
    if (!confirmAction) return;
    if (confirmAction.type === 'cancelTransfer' || confirmAction.type === 'cancelPending') {
      cancelTransfer.mutate({ transferId: confirmAction.transfer.id, reason: cancellationReason });
    } else if (confirmAction.type === 'cancelReturn') {
      cancelReturn.mutate({ transferId: confirmAction.transfer.id, reason: cancellationReason });
    }
    setConfirmAction(null);
    setCancellationReason('');
  };
  
  // Render loading state with skeleton
  if (isLoading) {
    return (
      <DashboardLayout>
        <MyAssetsSkeleton />
      </DashboardLayout>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <DashboardLayout>
        <div className="bg-gray-50 min-h-screen">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-red-700">Failed to load your assets. Please try again.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="space-y-6 bg-gray-50 min-h-screen">
        {/* Page Header */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-5 lg:p-6">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Package className="w-4 h-4 sm:w-6 sm:h-6 text-blue-800" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">My Assets</h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">View and manage assets assigned to you</p>
            </div>
          </div>
        </div>
        
        {/* Action queues above stats/tabs — self-hide when empty.
            Offences/accidents carry statutory clocks; monthly inspections are
            an ops compliance obligation. Drivers must not have to dig into a
            fleet asset card to find them. */}
        <MyTrafficOffences />
        <MyAccidents />
        <MyInspectionsDue />

        {/* Stats Cards — two glanceable metrics that map to the employee's
            actual questions: "what am I responsible for?" (Total Assets) and
            "is there anything I need to act on?" (Pending). The former
            "Categories" count was dropped: it answered no real employee
            question and duplicated the category-grouped sections already
            listed below. Two cards sit side-by-side at every breakpoint. */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <StatCard 
            title="Total Assets" 
            value={summary?.total_assets || 0} 
            icon={Package} 
            color="blue" 
          />
          <StatCard 
            title="Pending" 
            value={pendingCount} 
            icon={Inbox} 
            color="yellow" 
          />
        </div>
        
        {/* Tabs: My Assets + Transactions (red badge when pending) */}
        <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = effectiveTab === tab.id;
            const isTransactionsWithPending = tab.id === 'transactions' && tab.badge > 0;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`tab-btn ${isActive ? 'active' : ''}`}
              >
                <Icon className="w-4 h-4" />
                {tab.name}
                {tab.badge > 0 && (
                  <CountBadge count={tab.badge} theme={isTransactionsWithPending ? 'admin' : 'employee'} active={isActive} className="ml-1" />
                )}
              </button>
            );
          })}
        </div>
        
        {/* Tab Content */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          {/* My Assets Tab */}
          {effectiveTab === 'my-assets' && (
            <>
              {summary?.total_assets === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No assets assigned to you</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(summary?.assets_by_category || {}).map(([category, assets]) => {
                    const categoryCode = assets[0]?.asset_type?.category?.code;
                    const colors = getCategoryColorByCode(categoryCode);
                    return (
                    <div key={category}>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <span className={`w-6 h-6 ${colors.bg} rounded-full flex items-center justify-center`}>
                          <CategoryIcon code={categoryCode} className={`w-3 h-3 ${colors.text}`} />
                        </span>
                        {category}
                        <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-medium rounded-full bg-blue-800 text-white">
                          {assets.length}
                        </span>
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {assets.map(asset => (
                          <AssetCard 
                            key={asset.id} 
                            asset={asset} 
                            onTransfer={(a) => setTransferModal({ open: true, asset: a })}
                            onReturn={(a) => setReturnModal({ open: true, asset: a })}
                            onSendToWorkshop={(a) => setSendToWorkshopModal({ open: true, asset: a })}
                            onReturnFromWorkshop={(a) => setReturnFromWorkshopModal({ open: true, asset: a })}
                            onViewDetails={(a) => setDetailModal({ open: true, assetId: a.id })}
                            onViewLetter={handleViewLetter}
                            inspectionRow={inspectionsByAssetId[Number(asset.id)]}
                            onInspect={(a, row) => setInspectModal({
                              open: true,
                              vehicle: {
                                ...row,
                                // Prefer inspections-due baseline; fall back to
                                // asset-list mileage if the due row is sparse.
                                last_odometer_km: row?.last_odometer_km
                                  ?? a.fleet_vehicle_summary?.current_mileage
                                  ?? null,
                                last_odometer_date: row?.last_odometer_date
                                  ?? a.fleet_vehicle_summary?.current_mileage_date
                                  ?? null,
                                fleet_vehicle_summary: a.fleet_vehicle_summary,
                              },
                            })}
                            onReportAccident={(a) => setReportAccidentModal({
                              open: true,
                              vehicleNumber: a.fleet_vehicle?.vehicle_number
                                || a.asset_tag
                                || '',
                            })}
                          />
                        ))}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
          
          {/* Transactions Tab: Needs your action + Past */}
          {effectiveTab === 'transactions' && (
            <>
              <div className="space-y-6">
                <section>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Needs your action</h3>
                  {actionItems.length === 0 ? (
                    <p className="text-gray-500 text-sm">No pending transactions.</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {actionItems.map(({ t, type }) => {
                        if (type === 'incoming') return (
                          <IncomingTransferCard
                            key={t.id}
                            transfer={t}
                            onAccept={(x) => setAcceptModal({ open: true, transfer: x })}
                            onReject={(x) => setRejectModal({ open: true, transfer: x })}
                          />
                        );
                        if (type === 'batch_pending_receipt') return (
                          <BatchPendingReceiptCard
                            key={`batch-${t[0]?.batch_id}`}
                            transfers={t}
                            onAcknowledge={(transfers) => setBatchAckModal({ open: true, transfers })}
                          />
                        );
                        if (type === 'pending_receipt') return (
                          <PendingReceiptCard
                            key={t.id}
                            transfer={t}
                            onAcknowledge={(x) => setAcknowledgeReceiptModal({ open: true, transfer: x })}
                            onDecline={(x) => setRejectModal({ open: true, transfer: x })}
                          />
                        );
                        if (type === 'outgoing') return (
                          <OutgoingTransferCard key={t.id} transfer={t} onCancel={handleCancelTransfer} />
                        );
                        return (
                          <PendingReturnCard key={t.id} returnTransfer={t} onCancel={handleCancelReturn} />
                        );
                      })}
                    </div>
                  )}
                </section>
                <section>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Past transactions</h3>
                  {historyLoading && historyItems.length === 0 ? (
                    <p className="text-gray-500 text-sm">Loading...</p>
                  ) : historyItems.length === 0 ? (
                    <p className="text-gray-500 text-sm">No past transactions yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {historyItems.map((t) => (
                        <HistoryTransactionCard 
                          key={t.id} 
                          transfer={t} 
                          currentUserStaffId={user?.staff_id}
                          onViewDetails={(transfer) => setTransferDetailModal({ open: true, transfer })}
                        />
                      ))}
                    </div>
                  )}
                  {historyHasNext && (
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() => historyFetchNext()}
                        disabled={historyLoading}
                        className="btn btn-outline"
                      >
                        {historyLoading ? 'Loading...' : 'Load more'}
                      </button>
                    </div>
                  )}
                </section>
              </div>
            </>
          )}
        </div>
        
        {/* Modals */}
        <TransferModal
          isOpen={transferModal.open}
          onClose={() => setTransferModal({ open: false, asset: null })}
          asset={transferModal.asset}
          onSubmit={handleInitiateTransfer}
        />
        
        <ReturnModal
          isOpen={returnModal.open}
          onClose={() => setReturnModal({ open: false, asset: null })}
          asset={returnModal.asset}
          onSubmit={handleInitiateReturn}
          isLoading={initiateReturn.isPending}
        />

        <SendToWorkshopModal
          isOpen={sendToWorkshopModal.open}
          onClose={() => setSendToWorkshopModal({ open: false, asset: null })}
          asset={sendToWorkshopModal.asset}
          onSubmit={handleSendToWorkshop}
          isLoading={sendToWorkshop.isPending}
        />

        <ReturnFromWorkshopModal
          isOpen={returnFromWorkshopModal.open}
          onClose={() => setReturnFromWorkshopModal({ open: false, asset: null })}
          asset={returnFromWorkshopModal.asset}
          onSubmit={handleReturnFromWorkshop}
          isLoading={returnFromWorkshop.isPending}
        />

        <InspectVehicleModal
          isOpen={inspectModal.open}
          onClose={() => setInspectModal({ open: false, vehicle: null })}
          vehicle={inspectModal.vehicle}
          onSubmit={handleSubmitInspection}
          isLoading={submitInspection.isPending}
        />

        <ReportAccidentModal
          isOpen={reportAccidentModal.open}
          onClose={() => setReportAccidentModal({ open: false, vehicleNumber: '' })}
          theme="employee"
          presetVehicleNumber={reportAccidentModal.vehicleNumber}
        />
        
        <AcceptModal
          isOpen={acceptModal.open}
          onClose={() => setAcceptModal({ open: false, transfer: null })}
          transfer={acceptModal.transfer}
          onSubmit={handleAcceptTransfer}
          isLoading={acceptTransfer.isPending}
        />

        <AcknowledgeReceiptModal
          isOpen={acknowledgeReceiptModal.open}
          onClose={() => setAcknowledgeReceiptModal({ open: false, transfer: null })}
          transfer={acknowledgeReceiptModal.transfer}
          onSubmit={handleAcknowledgeReceipt}
          isLoading={acknowledgeReceipt.isPending}
          acknowledgementDeclaration={summary?.acknowledgement_declaration}
        />

        <BatchAcknowledgeReceiptModal
          isOpen={batchAckModal.open}
          onClose={() => setBatchAckModal({ open: false, transfers: [] })}
          transfers={batchAckModal.transfers}
          onSubmit={handleBatchAcknowledgeReceipt}
          isLoading={acknowledgeReceipt.isPending}
          acknowledgementDeclaration={summary?.acknowledgement_declaration}
        />

        <RejectModal
          isOpen={rejectModal.open}
          onClose={() => setRejectModal({ open: false, transfer: null })}
          transfer={rejectModal.transfer}
          onSubmit={handleRejectTransfer}
          isLoading={rejectTransfer.isPending}
        />
        
        {/* Asset Detail Modal - Employee View (hides purchase info) */}
        <AssetDetailModal
          isOpen={detailModal.open && !!detailModal.assetId}
          assetId={detailModal.assetId}
          onClose={() => setDetailModal({ open: false, assetId: null })}
          isEmployeeView={true}
        />
        
        {/* Transfer Detail Modal - For viewing transaction details with photos */}
        <TransferDetailModal
          isOpen={transferDetailModal.open && !!transferDetailModal.transfer}
          transfer={transferDetailModal.transfer}
          onClose={() => setTransferDetailModal({ open: false, transfer: null })}
          theme="employee"
        />
      </div>
      <ConfirmModal
        isOpen={!!confirmAction}
        title={
          confirmAction?.type === 'cancelReturn'
            ? 'Cancel Return Request'
            : 'Cancel Transfer'
        }
        confirmLabel="Yes, Cancel"
        cancelLabel="Keep"
        variant="danger"
        onConfirm={handleConfirmAction}
        onCancel={() => { setConfirmAction(null); setCancellationReason(''); }}
        theme="employee"
      >
        <p className="text-sm text-gray-700">
          {confirmAction?.type === 'cancelPending'
            ? 'Cancel? The asset will be returned to the previous holder or to inventory.'
            : confirmAction?.type === 'cancelReturn'
            ? 'Are you sure you want to cancel this return request?'
            : 'Are you sure you want to cancel this transfer?'}
        </p>
        <div className="mt-3">
          <label htmlFor="cancel-asset-reason" className="block text-sm font-medium text-gray-700 mb-1">
            Reason for cancellation
          </label>
          <textarea
            id="cancel-asset-reason"
            rows={3}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter reason for cancellation..."
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
          />
        </div>
      </ConfirmModal>

      {/* Letter Preview Modal */}
      {letterPreviewModal.open && letterPreviewModal.asset?.ack_letter_id && (
        <LetterPreviewModal
          letterId={letterPreviewModal.asset.ack_letter_id}
          letterReference={letterPreviewModal.asset.ack_letter_reference}
          assetTag={letterPreviewModal.asset.asset_tag}
          onClose={() => setLetterPreviewModal({ open: false, asset: null })}
        />
      )}
    </DashboardLayout>
  );
};

export default MyAssets;
